import { create } from 'zustand';
import { messageService } from '../react-app/services/apiService.js';
import socketIOService from '../react-app/services/socketIOService';
import { useChannelStore } from './channelStore.js';

const t = (minutesAgo) => Date.now() - minutesAgo * 60000;

const DEMO_MESSAGES = {
  'demo-f-general': [
    { id: 'dm1', channelId: 'demo-f-general', senderId: 'u-trevor', senderName: 'Trevor', author: 'Trevor', badge: 'Foundation', avatar: 'T', avatarBg: 'from-red-600 to-red-800', content: 'Just pushed the authentication updates. All services should automatically migrate to the new protocols within 24 hours.', timestamp: t(90) },
    { id: 'dm2', channelId: 'demo-f-general', senderId: 'u-sarah', senderName: 'Sarah', author: 'Sarah', badge: 'Labs', avatar: 'S', avatarBg: 'from-orange-400 to-orange-700', content: 'The Nexus v2 parallel compilation is insane. Cut build time from 3 minutes to under 2. Still some edge cases with complex state sync but wow... ⚠️', timestamp: t(60) },
    { id: 'dm3', channelId: 'demo-f-general', senderId: 'u-anderson', senderName: 'Anderson', author: 'Anderson', badge: 'Founder', avatar: 'A', avatarBg: 'from-red-600 via-blue-600 to-orange-400', content: 'Love seeing the Trinity infrastructure working in harmony. Foundation keeping everything secure, Labs pushing the boundaries, Corporation delivering production-ready tools. This is exactly the vision.', timestamp: t(45) },
    { id: 'dm4', channelId: 'demo-f-general', senderId: 'u-marcus', senderName: 'Marcus', author: 'Marcus', badge: 'Foundation', avatar: 'M', avatarBg: 'from-blue-500 to-blue-800', content: 'Security audit complete on the new auth layer. Zero critical findings. Two medium-severity items already patched. Green across the board. 🛡️', timestamp: t(20) },
  ],
  'demo-f-auth': [
    { id: 'da1', channelId: 'demo-f-auth', senderId: 'u-trevor', senderName: 'Trevor', author: 'Trevor', badge: 'Foundation', avatar: 'T', avatarBg: 'from-red-600 to-red-800', content: 'Foundation authentication services upgraded to v2.1.0. Enhanced security protocols now active across all AeThex infrastructure.', timestamp: t(120), type: 'system', label: 'FOUNDATION' },
    { id: 'da2', channelId: 'demo-f-auth', senderId: 'u-elara', senderName: 'Elara', author: 'Elara', badge: 'Foundation', avatar: 'E', avatarBg: 'from-purple-500 to-purple-800', content: 'OAuth2 + PKCE flow is fully tested. SSO integration with Authentik is seamless. Docs updated.', timestamp: t(50) },
  ],
  'demo-l-general': [
    { id: 'dl1', channelId: 'demo-l-general', senderId: 'u-sarah', senderName: 'Sarah', author: 'Sarah', badge: 'Labs', avatar: 'S', avatarBg: 'from-orange-400 to-orange-700', content: 'New experiment branch: real-time collaborative canvas. Think Figma but inside AeThex. Early proof of concept is looking 🔥', timestamp: t(180) },
    { id: 'dl2', channelId: 'demo-l-general', senderId: 'u-zin', senderName: 'Zin', author: 'Zin', badge: 'Labs', avatar: 'Z', avatarBg: 'from-cyan-500 to-cyan-800', content: 'Benchmarked the Nexus SDK against competitors. 40% faster cold start, 60% smaller bundle. These numbers are real.', timestamp: t(90) },
    { id: 'dl3', channelId: 'demo-l-general', senderId: 'u-anderson', senderName: 'Anderson', author: 'Anderson', badge: 'Founder', avatar: 'A', avatarBg: 'from-red-600 via-blue-600 to-orange-400', content: 'Labs is cooking. Ship it when it\'s ready — quality over speed here.', timestamp: t(30) },
  ],
  'demo-l-experiments': [
    { id: 'de1', channelId: 'demo-l-experiments', senderId: 'u-sarah', senderName: 'Sarah', author: 'Sarah', badge: 'Labs', avatar: 'S', avatarBg: 'from-orange-400 to-orange-700', content: 'Experiment #47: WebRTC mesh networking for sub-50ms voice latency. Results attached in the thread.', timestamp: t(200) },
    { id: 'de2', channelId: 'demo-l-experiments', senderId: 'u-zin', senderName: 'Zin', author: 'Zin', badge: 'Labs', avatar: 'Z', avatarBg: 'from-cyan-500 to-cyan-800', content: 'P2P fallback working. Achieved 34ms average in testing. Moving to staging next week.', timestamp: t(120) },
  ],
  'demo-c-general': [
    { id: 'dc1', channelId: 'demo-c-general', senderId: 'u-anderson', senderName: 'Anderson', author: 'Anderson', badge: 'Founder', avatar: 'A', avatarBg: 'from-red-600 via-blue-600 to-orange-400', content: 'AeThex Studio Pro users: New Railway deployment templates available. Optimized configurations for Foundation APIs, Corporation services, and Labs experiments.', timestamp: t(240), type: 'system', label: 'CORPORATION' },
    { id: 'dc2', channelId: 'demo-c-general', senderId: 'u-marcus', senderName: 'Marcus', author: 'Marcus', badge: 'Corporation', avatar: 'M', avatarBg: 'from-blue-500 to-blue-800', content: 'Q1 numbers are in. 340% YoY growth in enterprise subscriptions. The bot directory alone drove 40% of new signups.', timestamp: t(120) },
    { id: 'dc3', channelId: 'demo-c-general', senderId: 'u-trevor', senderName: 'Trevor', author: 'Trevor', badge: 'Foundation', avatar: 'T', avatarBg: 'from-red-600 to-red-800', content: 'Enterprise tier SLA is holding at 99.98% uptime. Foundation infrastructure is delivering. 💪', timestamp: t(60) },
  ],
};

export const useMessageStore = create((set, get) => ({
  messages: [],
  typingUsers: [],
  editingMessageId: null,
  editingContent: '',
  currentConversationId: null,
  isLoading: false,
  hasMore: true,
  socketConnected: false,
  pinnedMessages: [],
  threadMessages: new Map(),

  // Fetch messages from API
  fetchMessages: async (channelId) => {
    if (!channelId) {
      set({ messages: [], currentConversationId: null });
      return;
    }

    // Demo channels use local data — no API call
    if (channelId.startsWith('demo-')) {
      set({
        messages: DEMO_MESSAGES[channelId] || [],
        isLoading: false,
        currentConversationId: channelId,
        hasMore: false,
      });
      return;
    }

    set({ isLoading: true, currentConversationId: channelId });
    try {
      const response = await messageService.list(channelId, 50, 0);
      const messagesList = response.data || response;
      
      set({ 
        messages: Array.isArray(messagesList) ? messagesList : [],
        isLoading: false,
        hasMore: messagesList?.length === 50,
      });
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      set({ 
        isLoading: false 
      });
    }
  },

  // Initialize Socket.IO listeners
  initializeSocketListeners: () => {
    // Message events
    socketIOService.onNewMessage((message) => {
      get().addMessage(message);
    });

    socketIOService.onMessageEdited((data) => {
      get().updateMessage(data.messageId, {
        content: data.content,
        editedAt: new Date(),
      });
    });

    socketIOService.onMessageDeleted((data) => {
      get().deleteMessage(data.messageId);
    });

    socketIOService.onMessageReacted((data) => {
      const { messageId, emoji, userId, action } = data;
      if (action === 'add') {
        get().addReaction(messageId, emoji, userId);
      } else {
        get().removeReaction(messageId, emoji, userId);
      }
    });

    // Typing events
    socketIOService.onUsertyping((data) => {
      get().addTypingUser(data.userId, data.username);
    });

    socketIOService.onUserStoppedTyping((data) => {
      get().removeTypingUser(data.userId);
    });

    set(() => ({ socketConnected: true }));
  },

  setMessages: (messages) =>
    set(() => ({
      messages,
    })),

  addMessage: (message) => {
    // Track unread for channels other than the one currently open
    if (message.channelId) {
      const channelStore = useChannelStore.getState();
      const isMention = (message.text || message.content || '').includes('@');
      channelStore.incrementUnread?.(message.channelId, isMention);
    }
    set(state => ({ messages: [...state.messages, message] }));
  },

  prependMessages: (messages) =>
    set((state) => ({
      messages: [...messages, ...state.messages],
    })),

  updateMessage: (messageId, updates) =>
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === messageId ? { ...m, ...updates } : m
      ),
    })),

  deleteMessage: (messageId) =>
    set((state) => ({
      messages: state.messages.filter((m) => m.id !== messageId),
    })),

  startEditingMessage: (messageId, content) =>
    set(() => ({
      editingMessageId: messageId,
      editingContent: content,
    })),

  cancelEditingMessage: () =>
    set(() => ({
      editingMessageId: null,
      editingContent: '',
    })),

  saveEditedMessage: (messageId, newContent) => {
    const state = get();
    socketIOService.editMessage(state.currentConversationId, messageId, newContent);
    set(() => ({
      editingMessageId: null,
      editingContent: '',
    }));
  },

  addReaction: (messageId, emoji, userId) =>
    set((state) => ({
      messages: state.messages.map((m) => {
        if (m.id === messageId) {
          const reactions = m.reactions || [];
          const existingReaction = reactions.find((r) => r.emoji === emoji);

          if (existingReaction) {
            if (!existingReaction.users.includes(userId)) {
              existingReaction.users.push(userId);
            }
          } else {
            reactions.push({ emoji, users: [userId] });
          }

          return { ...m, reactions };
        }
        return m;
      }),
    })),

  removeReaction: (messageId, emoji, userId) =>
    set((state) => ({
      messages: state.messages.map((m) => {
        if (m.id === messageId) {
          const reactions = m.reactions || [];
          return {
            ...m,
            reactions: reactions
              .map((r) => ({
                ...r,
                users: r.emoji === emoji ? r.users.filter((u) => u !== userId) : r.users,
              }))
              .filter((r) => r.users.length > 0),
          };
        }
        return m;
      }),
    })),

  // Typing indicators
  addTypingUser: (userId, username) =>
    set((state) => {
      const exists = state.typingUsers.some((u) => u.id === userId);
      if (!exists) {
        return {
          typingUsers: [...state.typingUsers, { id: userId, username }],
        };
      }
      return state;
    }),

  removeTypingUser: (userId) =>
    set((state) => ({
      typingUsers: state.typingUsers.filter((u) => u.id !== userId),
    })),

  clearTypingUsers: () =>
    set(() => ({
      typingUsers: [],
    })),

  // Conversation management
  setCurrentConversationId: (conversationId) =>
    set(() => ({ currentConversationId: conversationId })),

  joinConversation: (conversationId) => {
    socketIOService.joinConversation(conversationId);
    get().setCurrentConversationId(conversationId);
  },

  leaveConversation: (conversationId) => {
    socketIOService.leaveConversation(conversationId);
    get().setCurrentConversationId(null);
    get().setMessages([]);
  },

  // Send message
  sendMessage: (content) => {
    const state = get();
    if (!state.currentConversationId || !content.trim()) return;

    const message = {
      id: `temp-${Date.now()}`,
      content,
      createdAt: new Date().toISOString(),
      senderId: localStorage.getItem('userId'),
      conversationId: state.currentConversationId,
      _sending: true,
    };

    get().addMessage(message);
    socketIOService.sendMessage(state.currentConversationId, message);
  },

  // Typing indicators
  notifyTyping: () => {
    const state = get();
    if (state.currentConversationId) {
      socketIOService.typingStart(state.currentConversationId);
    }
  },

  notifyStoppedTyping: () => {
    const state = get();
    if (state.currentConversationId) {
      socketIOService.typingStop(state.currentConversationId);
    }
  },

  // Reactions
  reactToMessage: (messageId, emoji) => {
    const state = get();
    if (state.currentConversationId) {
      socketIOService.reactToMessage(state.currentConversationId, messageId, emoji);
    }
  },

  // Pagination
  setIsLoading: (isLoading) =>
    set(() => ({ isLoading })),

  setHasMore: (hasMore) =>
    set(() => ({ hasMore })),

  loadMoreMessages: async () => {
    // Placeholder for pagination logic
    const state = get();
    if (!state.isLoading && state.hasMore && state.currentConversationId) {
      state.setIsLoading(true);
      // Load messages via API
      // then prependMessages
      state.setIsLoading(false);
    }
  },

  // Pinned messages
  pinMessage: (messageId) =>
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === messageId ? { ...m, pinned: true } : m
      ),
    })),

  unpinMessage: (messageId) =>
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === messageId ? { ...m, pinned: false } : m
      ),
    })),

  // Clear all
  clear: () =>
    set(() => ({
      messages: [],
      typingUsers: [],
      editingMessageId: null,
      editingContent: '',
      currentConversationId: null,
      isLoading: false,
      hasMore: true,
    })),
}));
